import { useEffect, useState } from "react";
import { PDFDocument } from "pdf-lib";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "react-toastify";
import { useSignature, FIELD_TYPES } from "./Components/SignatureContext";
import { UploadPdf } from "./Components/UploadPdf";
import PdfViewer from "./Components/PdfViewer";
import DocumentList from "./Components/DocumentList";
import StepEmailSign from "./Components/StepEmailSign";
import StepProgress from "./Components/StepProgress";
import Loader from "./Components/Loader";
import SuccessModal from "./Components/SuccessModal";
import PdfViewerModal from "./Components/PdfViewerModal";
import {
  submitSignature,
  submitDocument,
  sendMailLink,
} from "./API/DocuSealServices";

export default function DocumentMainPage() {
  const {
    pdfFile,
    setPdfFile,
    currentStep,
    setCurrentStep,
    blocks,
    signatures,
    resetSignatureContext,
  } = useSignature();

  const [referenceNumber, setReferenceNumber] = useState("");
  const [referenceType, setReferenceType] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  const [customer, setCustomer] = useState({
    customerFirstName: "",
    customerLastName: "",
    customerEmail: "",
    customerPhone: "",
  });

  useEffect(() => {
    setCurrentStep(0);
  }, [setCurrentStep]);

  useEffect(() => {
    if (currentStep === 0) {
      resetSignatureContext();
      setReferenceNumber("");
      setReferenceType("");
      setDocumentType("");
    }
  }, [currentStep]);

  const nextStep = () => setCurrentStep((s) => s + 1);
  const prevStep = () => setCurrentStep((s) => Math.max(s - 1, 0));

  const hasPdf = !!pdfFile;
  const hasBlocks = blocks.length > 0;
  const hasEmail = !!customer.customerEmail?.trim();
  const hasReference = !!referenceNumber.trim();
  const hasReferenceType = !!referenceType;
  const hasDocumentType = !!documentType;

  const allBlocksSigned = hasBlocks && blocks.every((b) => signatures[b.id]);

  const canGoNext =
    (currentStep === 1 && hasPdf && hasReferenceType && hasReference && hasDocumentType) ||
    (currentStep === 2 && hasBlocks) ||
    (currentStep === 3 && (hasEmail || hasBlocks));

  const buildNormalizedFields = async (isEmailFlow) => {
    if (!pdfFile) return [];

    const pdfBytes = await pdfFile.arrayBuffer();
    const pdfDoc = await PDFDocument.load(pdfBytes);

    const [firstPage] = pdfDoc.getPages();
    const { width: pageWidth, height: pageHeight } = firstPage.getSize();

    const getTypeSignNow = (fieldType) => {
      switch (fieldType) {
        case FIELD_TYPES.SIGNATURE:
          return "signature";
        case FIELD_TYPES.INITIAL:
          return "signature";
        case FIELD_TYPES.DATE:
          return "text";
      }
    };

    const getTypeEmail = (fieldType) => {
      switch (fieldType) {
        case FIELD_TYPES.SIGNATURE:
          return "signature";
        case FIELD_TYPES.INITIAL:
          return "initials";
        case FIELD_TYPES.DATE:
          return "date";
      }
    };

    return blocks.map((block, index) => {
      return {
        name: `${block.fieldType.toLowerCase()}_${index + 1}`,
        type: isEmailFlow
          ? getTypeEmail(block.fieldType)
          : getTypeSignNow(block.fieldType),
        required: true,
        areas: [
          {
            page: block.pageNumber ?? 1,
            x: Number((block.x / pageWidth).toFixed(4)),
            y: Number((block.y / pageHeight).toFixed(4)),
            w: Number((block.width / pageWidth).toFixed(4)),
            h: Number((block.height / pageHeight).toFixed(4)),
          },
        ],
      };
    });
  };

  const saveSignNow = async () => {
    try {
      setLoading(true);

      if (!pdfFile || !blocks.length) {
        throw new Error("PDF or fields missing");
      }

      if (!referenceNumber.trim()) {
        throw new Error("Reference number is required");
      }

      if (!customer?.customerEmail) {
        throw new Error("Customer email is required");
      }

      const fields = await buildNormalizedFields(false);

      const data = await submitDocument({
        file: pdfFile,
        fields,
        customer,
        referenceNumber,
        referenceType,
        documentType,
      });

      const signer = data.find((s) => s.role === "Signer") || data[0];

      const submitterId = signer?.id || signer?.submission_id;

      if (!submitterId) {
        throw new Error("Submitter ID not found");
      }

      const fieldUuids = signer?.fields || [];
      const values = {};

      blocks.forEach((block, index) => {
        const field = fieldUuids[index];
        if (!field?.uuid) return;

        if (block.fieldType === FIELD_TYPES.DATE) {
          values[field.uuid] = String(signatures[block.id] || "");
        } else {
          values[field.uuid] = signatures[block.id];
        }
      });

      await submitSignature({
        submitterId,
        values,
        completed: true,
      });

      toast.success("Document saved successfully");
      setShowSaved(true);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to save document");
    } finally {
      setLoading(false);
    }
  };

  const makeSubmissionSendEmail = async () => {
    try {
      setLoading(true);

      if (!customer?.customerEmail?.trim()) {
        toast.error("Customer email is required");
        return;
      }

      if (!pdfFile || !blocks.length) {
        throw new Error("PDF or fields missing");
      }

      if (!referenceNumber.trim()) {
        throw new Error("Reference number is required");
      }

      const fields = await buildNormalizedFields(true);

      const data = await submitDocument({
        file: pdfFile,
        fields,
        customer,
        referenceNumber,
        referenceType,
        documentType,
      });

      const signer = data.find((s) => s.role === "Signer") || data[0];

      const submitterId = signer?.id || signer?.submission_id;

      if (!submitterId) {
        throw new Error("Submitter ID not found");
      }

      const signUrl = signer?.embed_src;

      if (!signUrl) {
        throw new Error("Signing URL not found");
      }

      await sendMailLink({
        to: customer.customerEmail,
        token: signer.slug,
      });

      toast.success(`Document submitted for ${customer.customerEmail}`);

      setShowSaved(true);
      setShowSuccessModal(true);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to send email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {currentStep > 0 && (
        <nav className="sticky top-0 z-40 border-b bg-white/80 backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
            <div className="w-32">
              <button
                onClick={prevStep}
                className="flex items-center gap-1 rounded-lg border px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
              >
                <ArrowLeft size={14} /> Back
              </button>
            </div>

            <StepProgress
              currentStep={currentStep}
              onStepClick={setCurrentStep}
            />

            <div className="flex w-32 justify-center">
              {[1, 2, 3].includes(currentStep) && (
                <button
                  disabled={!canGoNext}
                  onClick={nextStep}
                  className={`flex items-center gap-1 rounded-lg px-4 py-2 text-sm text-white
                    ${
                      canGoNext
                        ? "bg-sky-500 hover:bg-sky-600"
                        : "bg-slate-300 cursor-not-allowed"
                    }`}
                >
                  Next <ArrowRight size={14} />
                </button>
              )}

              {currentStep === 4 && (
                <button
                  disabled={!allBlocksSigned || loading}
                  onClick={saveSignNow}
                  className={`flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-white
                    ${
                      allBlocksSigned && !loading
                        ? "bg-sky-500 hover:bg-sky-600"
                        : "bg-gray-600 cursor-not-allowed"
                    }`}
                >
                  <CheckCircle2 size={14} /> Submit
                </button>
              )}
            </div>
          </div>
        </nav>
      )}

      <main className="mx-auto max-w-6xl p-6">
        {currentStep === 0 && <DocumentList nextStep={nextStep} />}

        {currentStep === 1 && (
          <UploadPdf
            selectedFile={pdfFile}
            onFileSelect={setPdfFile}
            onFileClear={() => setPdfFile(null)}
            referenceNumber={referenceNumber}
            onReferenceChange={setReferenceNumber}
            referenceType={referenceType}
            onReferenceTypeChange={(val) => { setReferenceType(val); setDocumentType(""); }}
            documentType={documentType}
            onDocumentTypeChange={setDocumentType}
            customer={customer}
            onCustomerChange={setCustomer}
          />
        )}

        {currentStep === 2 && <PdfViewer currentStep={currentStep} />}

        {currentStep === 3 && (
          <StepEmailSign
            customerEmail={customer.customerEmail}
            onSignNow={() => setCurrentStep(4)}
            onSendMail={makeSubmissionSendEmail}
          />
        )}
      </main>

      <Loader show={loading} label="Loading…" />

      <SuccessModal
        open={showSaved}
        title="All Signed!"
        message="Your document has been successfully signed and saved."
        onAction={() => window.location.reload()}
      />

      <SuccessModal
        open={showSuccessModal}
        title="Submitted for Signature"
        message="The document has been submitted. The customer can sign it via the emailed link or from the Document Search page using their reference number."
        onAction={() => window.location.reload()}
      />

      {currentStep === 4 && (
        <PdfViewerModal
          currentStep={currentStep}
          onClose={() => setCurrentStep(3)}
          onSubmit={saveSignNow}
        />
      )}
    </div>
  );
}
